import React, { useState, useEffect } from 'react';
import { getAllDoctors, createDoctor, updateDoctor, deleteDoctor as deleteDoctorApi } from '../../api';
import styles from './CreateDoctor.module.css';

const initialDoctorState = {
  name: '',
  designationDegree: '',
  registrationNumber: '',
  experience: '',
  phoneNumber: '',
  fees: '',
  slotAvailability: false,
  availableSlots: [],
};

const CreateDoctor = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [form, setForm] = useState(initialDoctorState);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const data = await getAllDoctors();
      setDoctors(data);
    } catch (error) {
      showAlert('error', 'Error fetching doctors');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
  };

  const resetForm = () => {
    setForm(initialDoctorState);
    setSelectedDoctor(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (doctor) => {
    setForm({ ...doctor, availableSlots: doctor.availableSlots || [] });
    setSelectedDoctor(doctor);
    setShowEditModal(true);
  };

  const handleView = (doctor) => {
    setSelectedDoctor(doctor);
    setShowViewModal(true);
  };

  const handleDelete = (doctor) => {
    setSelectedDoctor(doctor);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddSlot = () => {
    if (selectedDate && selectedTime) {
      const newSlot = { date: selectedDate, time: selectedTime };
      setForm((prev) => ({
        ...prev,
        availableSlots: [...prev.availableSlots, newSlot],
      }));
      setSelectedDate('');
      setSelectedTime('');
    }
  };

  const handleRemoveSlot = (index) => {
    setForm((prev) => ({
      ...prev,
      availableSlots: prev.availableSlots.filter((_, i) => i !== index),
    }));
  };

  const filteredDoctors = doctors.filter(doctor =>
    doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.designationDegree.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.phoneNumber.includes(searchTerm)
  );

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.designationDegree || !form.registrationNumber || !form.experience || !form.phoneNumber || !form.fees) {
      showAlert('error', 'All fields are required');
      return;
    }

    try {
      await createDoctor(form);
      setShowAddModal(false);
      showAlert('success', 'Doctor added successfully!');
      resetForm();
      fetchDoctors();
    } catch (error) {
      showAlert('error', 'Error adding doctor');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.designationDegree || !form.registrationNumber || !form.experience || !form.phoneNumber || !form.fees) {
      showAlert('error', 'All fields are required');
      return;
    }

    try {
      await updateDoctor(selectedDoctor._id, form);
      setShowEditModal(false);
      showAlert('success', 'Doctor updated successfully!');
      resetForm();
      fetchDoctors();
    } catch (error) {
      showAlert('error', 'Error updating doctor');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDoctorApi(selectedDoctor._id);
      setShowDeleteModal(false);
      showAlert('success', 'Doctor deleted successfully!');
      resetForm();
      fetchDoctors();
    } catch (error) {
      showAlert('error', 'Error deleting doctor');
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading doctors...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Doctor Management</h2>
        <p>Create, edit, delete, and view doctor information</p>
      </div>

      {alert.show && (
        <div className={`${styles.alert} ${styles[`alert${alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}`]}`}>
          {alert.message}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleAdd}
        >
          <span>+</span> Add New Doctor
        </button>
      </div>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search doctors by name, designation, registration number, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        {filteredDoctors.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No doctors found</h3>
            <p>{searchTerm ? 'Try adjusting your search terms' : 'Start by adding a new doctor'}</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Doctor Name</th>
                <th>Designation & Degree</th>
                <th>Registration Number</th>
                <th>Experience</th>
                <th>Phone Number</th>
                <th>Fees</th>
                <th>Availability</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {filteredDoctors.map((doctor) => (
                <tr key={doctor._id}>
                  <td>{doctor.name}</td>
                  <td>{doctor.designationDegree}</td>
                  <td>{doctor.registrationNumber}</td>
                  <td>{doctor.experience} years</td>
                  <td>{doctor.phoneNumber}</td>
                  <td>${doctor.fees}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${doctor.slotAvailability ? styles.statusAvailable : styles.statusNotAvailable}`}>
                      {doctor.slotAvailability ? 'Available' : 'Not Available'}
                    </span>
                  </td>
                  <td className={styles.tableActions}>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
                      onClick={() => handleView(doctor)}
                    >
                      View
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}
                      onClick={() => handleEdit(doctor)}
                    >
                      Edit
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                      onClick={() => handleDelete(doctor)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Add New Doctor</h3>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Doctor Name *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter doctor name"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Designation & Degree *</label>
                <input
                  type="text"
                  name="designationDegree"
                  value={form.designationDegree}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="e.g., Cardiologist, MBBS, MD"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Registration Number *</label>
                <input
                  type="text"
                  name="registrationNumber"
                  value={form.registrationNumber}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter registration number"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Experience (years) *</label>
                <input
                  type="number"
                  name="experience"
                  value={form.experience}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter years of experience"
                  min="0"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter phone number"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Fees ($) *</label>
                <input
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter consultation fees"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    name="slotAvailability"
                    checked={form.slotAvailability}
                    onChange={handleFormChange}
                    className={styles.checkbox}
                    id="slotAvailability"
                  />
                  <label htmlFor="slotAvailability" className={styles.checkboxLabel}>
                    Slot Booking Available
                  </label>
                </div>
                {form.slotAvailability && (
                  <div className={styles.slotBookingSection}>
                    <div className={styles.slotInputs}>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={styles.input}
                      />
                      <input
                        type="time"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className={styles.input}
                      />
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}
                        onClick={handleAddSlot}
                      >
                        Add Slot
                      </button>
                    </div>
                    <ul className={styles.slotList}>
                      {(form.availableSlots || []).map((slot, idx) => (
                        <li key={idx} className={styles.slotItem}>
                          {slot.date} at {slot.time}
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                            onClick={() => handleRemoveSlot(idx)}
                            style={{ marginLeft: '8px' }}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnSuccess}`}
                >
                  Add Doctor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Edit Doctor</h3>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Doctor Name *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter doctor name"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Designation & Degree *</label>
                <input
                  type="text"
                  name="designationDegree"
                  value={form.designationDegree}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="e.g., Cardiologist, MBBS, MD"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Registration Number *</label>
                <input
                  type="text"
                  name="registrationNumber"
                  value={form.registrationNumber}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter registration number"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Experience (years) *</label>
                <input
                  type="number"
                  name="experience"
                  value={form.experience}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter years of experience"
                  min="0"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter phone number"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Fees ($) *</label>
                <input
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter consultation fees"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    name="slotAvailability"
                    checked={form.slotAvailability}
                    onChange={handleFormChange}
                    className={styles.checkbox}
                    id="editSlotAvailability"
                  />
                  <label htmlFor="editSlotAvailability" className={styles.checkboxLabel}>
                    Slot Booking Available
                  </label>
                </div>
                {form.slotAvailability && (
                  <div className={styles.slotBookingSection}>
                    <div className={styles.slotInputs}>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={styles.input}
                      />
                      <input
                        type="time"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className={styles.input}
                      />
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}
                        onClick={handleAddSlot}
                      >
                        Add Slot
                      </button>
                    </div>
                    <ul className={styles.slotList}>
                      {(form.availableSlots || []).map((slot, idx) => (
                        <li key={idx} className={styles.slotItem}>
                          {slot.date} at {slot.time}
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                            onClick={() => handleRemoveSlot(idx)}
                            style={{ marginLeft: '8px' }}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnSuccess}`}
                >
                  Update Doctor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedDoctor && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Doctor Details</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowViewModal(false)}
              >
                ×
              </button>
            </div>

            <div className={styles.viewDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Doctor Name</span>
                <span className={styles.detailValue}>{selectedDoctor.name}</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Designation & Degree</span>
                <span className={styles.detailValue}>{selectedDoctor.designationDegree}</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Registration Number</span>
                <span className={styles.detailValue}>{selectedDoctor.registrationNumber}</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Experience</span>
                <span className={styles.detailValue}>{selectedDoctor.experience} years</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Phone Number</span>
                <span className={styles.detailValue}>{selectedDoctor.phoneNumber}</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Consultation Fees</span>
                <span className={styles.detailValue}>${selectedDoctor.fees}</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Slot Availability</span>
                <span className={`${styles.statusBadge} ${selectedDoctor.slotAvailability ? styles.statusAvailable : styles.statusNotAvailable}`}>
                  {selectedDoctor.slotAvailability ? 'Available' : 'Not Available'}
                </span>
              </div>

              {selectedDoctor.slotAvailability && (selectedDoctor.availableSlots || []).length > 0 && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Available Slots</span>
                  <ul className={styles.slotList}>
                    {(selectedDoctor.availableSlots || []).map((slot, idx) => (
                      <li key={idx} className={styles.slotItem}>
                        {slot.date} at {slot.time}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedDoctor && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Confirm Delete</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to delete <b>Dr. {selectedDoctor.name}</b>?</p>
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDoctor; 